/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, FontWeights, PrimaryButton, Separator, Shimmer, Spinner } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { ITooltipStyles } from '@fluentui/react/lib/Tooltip';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import { default as React } from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { ResourceKeyCell } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/ResourceKeyCell';
import { BasicModal } from 'src/core/components/BasicModal';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { CopyButton } from 'src/core/components/CopyButton';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { Page } from 'src/core/components/Page';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logButtonClick, logTextEntry, logToggleChange } from 'src/core/services/telemetry/eventsNamesTemplate';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { AmlWarningModal } from 'src/customText/core/components/AmlWarningModal';
import { CustomTextProjectStore } from 'src/customText/core/stores/CustomTextProjectStore';
import { constructAmlProjectUrl } from 'src/customText/core/utils/constructAmlProjectUrl';
import { AzureMLConnectionModal } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';
import { TooltipProps } from 'src/modules/labeling/components/Tooltip';

type CustomTextProjectSettingsDto = Pick<CustomTextProjectStore, 'description' | 'multiLingual' | 'oosThreshold'>;

type HorizontalTextRendererType = { title: string; message: string };

const messages = defineMessages({
    value: { id: 'customText.customTextSettingsPage.value', defaultMessage: 'Value' },
    saveButton: { id: 'customText.customTextSettingsPage.saveButton', defaultMessage: 'Save' },
    nameLabel: { id: 'customText.customTextSettingsPage.nameLabel', defaultMessage: 'Project Name' },
    enabledToggle: { id: 'customText.customTextSettingsPage.enabledToggle', defaultMessage: 'Enabled' },
    pageTitle: { id: 'customText.customTextSettingsPage.pageTitle', defaultMessage: 'Project settings' },
    languageTitle: { id: 'customText.customTextSettingsPage.languageTitle', defaultMessage: 'Language' },
    experimental: { id: 'customText.customTextSettingsPage.experimental', defaultMessage: 'Experimental' },
    labelsFileLabel: { id: 'customText.customTextSettingsPage.labelsFileLabel', defaultMessage: 'Labels File:' },
    primaryKeyLabel: { id: 'customText.customTextSettingsPage.primaryKeyLabel', defaultMessage: 'Primary key:' },
    notEnabledToggle: { id: 'customText.customTextSettingsPage.notEnabledToggle', defaultMessage: 'Not enabled' },
    descriptionLabel: { id: 'customText.customTextSettingsPage.descriptionLabel', defaultMessage: 'Project description' },
    nameSectionTitle: { id: 'customText.customTextSettingsPage.nameSectionTitle', defaultMessage: 'Name and description' },
    storageAccountName: { id: 'customText.customTextSettingsPage.storageAccountName', defaultMessage: 'Storage account:' },
    azureResourceLabel: { id: 'customText.customTextSettingsPage.azureResourceLabel', defaultMessage: 'Language resource:' },
    multiLingualTitle: { id: 'customText.customTextSettingsPage.multiLingualTitle', defaultMessage: 'Multi-lingual dataset:' },
    multiLingualLabel: { id: 'customText.customTextSettingsPage.multiLingualLabel', defaultMessage: 'Multi-lingual dataset' },
    updateRunningToast: { id: 'customText.customTextSettingsPage.updateRunningToast', defaultMessage: 'Updating project ...' },
    blobStoreContainer: { id: 'customText.customTextSettingsPage.blobStoreContainer', defaultMessage: 'Blob store container:' },
    azureResourceTitle: { id: 'customText.customTextSettingsPage.azureResourceTitle', defaultMessage: 'Azure Language resource' },
    primaryLanguageTitle: { id: 'customText.customTextSettingsPage.primaryLanguageTitle', defaultMessage: 'Text primary language:' },
    updateFailureToast: { id: 'customText.customTextSettingsPage.updateFailureToast', defaultMessage: 'Failed to update the project' },
    updateCompletedToast: { id: 'customText.customTextSettingsPage.updateCompletedToast', defaultMessage: 'Project successfully updated' },
    oosThresholdLabel: { id: 'customText.customTextSettingsPage.oosThresholdLabel', defaultMessage: 'None score threshold' },
    oosThresholdErrorMsg: {
        id: 'customText.customTextSettingsPage.oosThresholdErrorMsg',
        defaultMessage: 'Value should be greater that or equal to 0 and less than 1'
    },
    oosThresholdTooltip: {
        id: 'customText.customTextSettingsPage.oosThresholdTooltip',
        defaultMessage: 'If the top intent’s score is below this threshold, the top intent will be automatically detected as None'
    },
    changeMultiLingual: {
        id: 'customText.customTextSettingsPage.changeMultiLingual',
        defaultMessage: 'Once multiple languages have been enabled, they cannot be disabled.'
    },
    azureResourceDescription: {
        id: 'customText.customTextSettingsPage.azureResourceDescription',
        defaultMessage: `A Language resource is created for you when you create your
        Language account. The associated key is used for publishing your project`
    },
    amlConnectionTitle: {
        id: 'customText.customTextSettingsPage.amlConnectionTitle',
        defaultMessage: 'Manage Azure Machine Learning connections'
    },
    amlConnectionDescription: {
        id: 'customText.customTextSettingsPage.amlConnectionDescription',
        defaultMessage: `Now you can bring your data labeling projects into Azure Machine Learning for
        additional capabilities.`
    },
    amlConnectionDescriptionLink: {
        id: 'customText.customTextSettingsPage.amlConnectionDescriptionLink',
        defaultMessage: 'Learn more about data labeling with Azure Machine Learning'
    },
    amlConnectionButtonTitle: {
        id: 'customText.customTextSettingsPage.amlConnectionButtonTitle',
        defaultMessage: 'Connect to the labeling experience in Azure Machine Learning'
    },
    amlDisconnectionButtonTitle: {
        id: 'customText.customTextSettingsPage.amlDisconnectionButtonTitle',
        defaultMessage: 'Disconnect from Azure Machine Learning'
    },
    amlConnectionButtonLabel: {
        id: 'customText.customTextSettingsPage.amlConnectionButtonLabel',
        defaultMessage: 'Connect to Azure Machine Learning'
    },
    connectionStatus: {
        id: 'customText.customTextSettingsPage.connectionStatus',
        defaultMessage: 'Connection status:'
    },
    connected: {
        id: 'customText.customTextSettingsPage.connected',
        defaultMessage: 'Connected'
    },
    labelingStatus: {
        id: 'customText.customTextSettingsPage.labelingStatus',
        defaultMessage: 'Labeling status:'
    },
    labelingStatusInLS: {
        id: 'customText.customTextSettingsPage.labelingStatusInLS',
        defaultMessage: 'Enabled in Language Studio'
    },
    switchLabelingLocation: {
        id: 'customText.customTextSettingsPage.switchLabelingLocation',
        defaultMessage: 'Switch labeling activity location'
    },
    labelingStatusInAml: {
        id: 'customText.customTextSettingsPage.labelingStatusInAml',
        defaultMessage: 'Disabled in Language Studio'
    },
    goToAml: {
        id: 'customText.customTextSettingsPage.goToAml',
        defaultMessage: 'Go to Azure Machine Learning'
    },
    azureBlobDataStore: {
        id: 'customText.customTextSettingsPage.azureBlobDataStore',
        defaultMessage: 'Azure blob datastore:'
    },
    azureMLWorkspace: {
        id: 'customText.customTextSettingsPage.azureMLWorkspace',
        defaultMessage: 'Azure Machine Learning workspace:'
    },
    projectName: {
        id: 'customText.customTextSettingsPage.projectName',
        defaultMessage: 'Project name:'
    },
    labelingTaskType: {
        id: 'customText.customTextSettingsPage.labelingTaskType',
        defaultMessage: 'Labeling task type:'
    },
    language: {
        id: 'customText.customTextSettingsPage.language',
        defaultMessage: 'Language:'
    },
    incrementalRefresh: {
        id: 'customText.customTextSettingsPage.incrementalRefresh',
        defaultMessage: 'Incremental refresh:'
    },
    outsourcing: {
        id: 'customText.customTextSettingsPage.outsourcing',
        defaultMessage: 'Outsourcing:'
    },
    labelingInstruction: {
        id: 'customText.customTextSettingsPage.labelingInstruction',
        defaultMessage: 'Labeling instructions:'
    },
    disconnectLabel: {
        id: 'customText.customTextSettingsPage.disconnectLabel',
        defaultMessage: 'Disconnect from Azure Machine Learning?'
    },
    disconnectDescription: {
        id: 'customText.customTextSettingsPage.disconnectDescription',
        defaultMessage: 'Disconnecting cannot be undone, and you won’t be able to use any Azure Machine Learning features for this project.'
    },
    none: {
        id: 'customText.customTextSettingsPage.none',
        defaultMessage: 'None'
    },
    provided: {
        id: 'customText.customTextSettingsPage.provided',
        defaultMessage: 'Provided'
    },
    cancel: {
        id: 'customText.customTextSettingsPage.cancel',
        defaultMessage: 'Cancel'
    },
    disconnect: {
        id: 'customText.customTextSettingsPage.disconnect',
        defaultMessage: 'Disconnect'
    },
    classificationInstructionModalTitle: {
        id: 'customText.customTextSettingsPage.classificationInstructionModalTitle',
        defaultMessage: 'There must be at least two classes created in your project'
    },
    nerInstructionModalTitle: {
        id: 'customText.customTextSettingsPage.nerInstructionModalTitle',
        defaultMessage: 'There must be at least one entity created in your project'
    },
    classificationInstructionModalContent: {
        id: 'customText.customTextSettingsPage.classificationInstructionModalContent',
        defaultMessage: 'Before you to connect to with Azure Machine Learning, you need to have at least two classes created.'
    },
    nerInstructionModalContent: {
        id: 'customText.customTextSettingsPage.nerInstructionModalContent',
        defaultMessage: 'Before you to connect to with Azure Machine Learning, you need to have at least one entity created.'
    },
    gotIt: {
        id: 'customText.customTextSettingsPage.gotIt',
        defaultMessage: 'Got it'
    },
    multiLingual: {
        id: 'customText.customTextSettingsPage.multiLingual',
        defaultMessage: 'You can not connect to Azure Machine Learning if your project is multi-lingual.'
    }
});

const rBACTooltipStyles = classNamesFunction<TooltipProps, ITooltipStyles>()({ root: { width: '400px' } });
const childrenGapConstant = 4;
const fontWeight = FontWeights.semibold as Property.FontWeight;

const isFormDirty = (currentProject: CustomTextProjectStore, currentForm: CustomTextProjectSettingsDto) =>
    currentForm.multiLingual !== currentProject.multiLingual ||
    currentForm.description !== currentProject.description ||
    currentForm.oosThreshold !== currentProject.oosThreshold;

export const HorizontalTextRenderer = (props: HorizontalTextRendererType) => {
    const { title, message } = props;

    return (
        <Stack horizontal tokens={{ childrenGap: childrenGapConstant }} styles={{ root: { marginBottom: '10px' } }}>
            <LuisText fontWeight={fontWeight}>{title}</LuisText>
            <LuisText>
                &nbsp;
                {message}
            </LuisText>
        </Stack>
    );
};

export const CustomTextSettingsPage = observer(() => {
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { rootStore, dispatcher } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();
    const updateTracker = useProgressTracker();
    const getProjectInfoTracker = useProgressTracker();
    const disconnectTracker = useProgressTracker();
    const history = useHistory();
    const location = useLocation();
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canListResourceKeys } = mapUserRoleToUserActions(userRole);

    const currentProject = rootStore.currentCustomTextProjectStore;
    const { isLabelingLocked, amlConnectionInfo, projectKind, multiLingual: isMultiLingual } = currentProject;
    const isExtractionProject = projectKind === 'CustomEntityRecognition';
    const currentResource = rootStore.azureStore.session.csAccountStore;
    const { formState, formStateDto, updateFormState } = useFormState<
        Pick<CustomTextProjectStore, 'description' | 'multiLingual' | 'oosThreshold'>
    >({
        multiLingual: [currentProject.multiLingual],
        description: [currentProject.description],
        oosThreshold: [
            currentProject.oosThreshold,
            FormValidators.createNumberRangeValidator({ range: [0, 1], maxMode: 'exclude', errorMessage: messages.oosThresholdErrorMsg })
        ]
    });

    const [
        isDisconnectAmlProjectModalOpen,
        { setTrue: showDisconnectAmlProjectModalOpen, setFalse: hideDisconnectAmlProjectModalOpen }
    ] = useBoolean(false);
    const [isAzureMLConnectionModalHidden, { setTrue: hideIsAzureMLConnectionModal, setFalse: showAzureMLConnectionModal }] = useBoolean(
        true
    );
    const [isLabelInstructionModalOpen, { setTrue: showLabelInstructionModal, setFalse: hideLabelInstructionModal }] = useBoolean(false);

    const [oosThresholdText, setOosThresholdText] = React.useState<string>(`${currentProject.oosThreshold}`);
    const [amlProjectUrl, setAmlProjectUrl] = React.useState('');

    const projectLanguage =
        rootStore.customTextCultureStorehouse.items.find(c => c.code === currentProject.culture)?.name ?? currentProject.culture;
    const { amlProjectPath } = currentProject;

    const handleOpenAzureMLConnectionModal = () => {
        if (currentProject?.extractionStore?.entities?.length < 1) {
            showLabelInstructionModal();
        } else if (currentProject?.classificationStore?.classes?.length < 2) {
            showLabelInstructionModal();
        } else {
            logButtonClick(
                isExtractionProject ? 'ner' : 'classification',
                'AMLPivot',
                'AML:ConnectToAzureMachineLearning',
                'AzureMachineLearning'
            );
            showAzureMLConnectionModal();
            hideDisconnectAmlProjectModalOpen();
        }
    };

    const handleAmlTermination = () => {
        dispatcher.dispatch('terminateAmlConnection', { tracker: disconnectTracker });
    };

    const updateProject = () => {
        const { description, multiLingual, oosThreshold } = formStateDto;

        if (!isFormDirty(currentProject, formStateDto)) {
            return;
        }

        if (currentProject.projectKind !== 'CustomEntityRecognition' && currentProject.oosThreshold !== oosThreshold) {
            logTextEntry(serviceName, 'Settings', 'OOSEntry', currentProject.id);
        }

        dispatcher.dispatch('updateCustomTextProject', {
            tracker: updateTracker,
            name: currentProject.name,
            description,
            multiLingual,
            oosThreshold,
            toastData: {
                failed: { title: localize(messages.updateFailureToast) },
                running: { title: localize(messages.updateRunningToast) },
                completed: { title: localize(messages.updateCompletedToast) }
            }
        });
    };

    const resetForm = () =>
        updateFormState({
            description: currentProject.description,
            multiLingual: currentProject.multiLingual,
            oosThreshold: currentProject.oosThreshold
        });

    /**
     * Buttons enabled when form is dirty and no
     * request is in flight.
     */
    const areButtonsEnabled = () => {
        if (isFormDirty(currentProject, formStateDto)) {
            return true;
        }

        if (updateTracker.isRunning) {
            return false;
        }

        return false;
    };

    const items: ICommandBarItemProps[] = [
        {
            key: 'save',
            onClick: updateProject,
            disabled: !areButtonsEnabled(),
            iconProps: { iconName: 'Save' },
            text: localize(messages.saveButton)
        },
        {
            key: 'cancel',
            onClick: resetForm,
            disabled: !areButtonsEnabled(),
            iconProps: { iconName: 'Cancel' },
            text: localize(globalMessages.cancel)
        }
    ];

    const renderTitles = (title: string) => (
        <LuisText variant="mediumPlus" fontWeight={600}>
            {title}
        </LuisText>
    );

    const renderLabeledText = (label: string, text: string | React.ReactNode) => (
        <Stack horizontal={isLargeOrGreater} verticalAlign="center">
            <Label styles={{ root: { width: '250px' } }}>{label}</Label>
            <LuisText>{text}</LuisText>
        </Stack>
    );

    React.useEffect(() => {
        if (location.hash) {
            const element = document.getElementById('amlConnectionButtonId');
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (FeatureToggles.AMLIntegration && amlProjectPath) {
            dispatcher.dispatch('getAmlProjectInfo', { tracker: getProjectInfoTracker });
        }

        const url = constructAmlProjectUrl(amlProjectPath);
        setAmlProjectUrl(url);
    }, [amlProjectPath]);

    return (
        <Page
            size="container-fluid"
            title={localize(messages.pageTitle)}
            pageName="CustomTextProjectSettingsPage"
            data-id="CustomTextSettings">
            <EditableTitle title={localize(messages.pageTitle)} />
            <UnsavedChangesPrompt
                isPageDirty={isFormDirty(currentProject, formStateDto)}
                promptPath={routeTable.customText.projectSettingsPage(currentProject.name)}
            />

            <Stack tokens={{ childrenGap: 25 }}>
                <LuisCommandBar items={items} />
                <Stack tokens={{ childrenGap: 15 }}>
                    {renderTitles(localize(messages.nameSectionTitle))}

                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
                        <LuisTextField
                            disabled
                            styles={{ root: { width: 400 } }}
                            value={currentProject.name}
                            label={localize(messages.nameLabel)}
                        />
                        <CopyButton textToCopy={currentProject.name} label={localize(messages.nameLabel)} />
                    </Stack>

                    {renderLabeledText(localize(messages.storageAccountName), currentResource.ownedStorageAccountInfo.name)}
                    {renderLabeledText(localize(messages.blobStoreContainer), currentProject.containerName)}

                    <RBACTooltip
                        hidden={canWrite}
                        styles={rBACTooltipStyles}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="projectDescriptionFieldLanguageToolTip"
                        componentSurroundedByToolTip={
                            <LuisTextField
                                id="descriptionId"
                                multiline
                                disabled={!canWrite}
                                onEnterKeyUp={updateProject}
                                data-automation-id="descriptionTextBox"
                                styles={{ root: { width: isLargeOrGreater ? 400 : 160 } }}
                                label={localize(messages.descriptionLabel)}
                                value={formState.fields.description.value}
                                onChange={(_, value) => updateFormState({ description: value })}
                            />
                        }
                    />
                </Stack>
                {currentProject.projectKind !== 'CustomEntityRecognition' && [
                    <Separator />,

                    <Stack tokens={{ childrenGap: 15 }}>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            <Stack horizontal verticalAlign="center">
                                <LuisText variant="mediumPlus" fontWeight={600}>
                                    {localize(messages.oosThresholdLabel)}
                                </LuisText>

                                <CalloutButton content={localize(messages.oosThresholdTooltip)} />
                            </Stack>
                        </Stack>

                        <RBACTooltip
                            hidden={canWrite}
                            styles={rBACTooltipStyles}
                            displayedUserRole={displayedUserRole}
                            data-automation-id="Threshold"
                            componentSurroundedByToolTip={
                                <LuisTextField
                                    min={0}
                                    max={1}
                                    type="number"
                                    disabled={!canWrite}
                                    value={oosThresholdText}
                                    label={localize(messages.value)}
                                    errorMessage={formState.fields.oosThreshold.errorMessage}
                                    styles={{ root: { width: isLargeOrGreater ? 400 : 160 } }}
                                    onChange={(_e, value) => {
                                        setOosThresholdText(value);
                                        updateFormState({ oosThreshold: parseFloat(value) });
                                    }}
                                />
                            }
                        />
                    </Stack>
                ]}
                <Separator />

                <Stack tokens={{ childrenGap: 15 }}>
                    {renderTitles(localize(messages.languageTitle))}

                    {renderLabeledText(localize(messages.primaryLanguageTitle), projectLanguage)}
                    {renderLabeledText(
                        localize(messages.multiLingualTitle),
                        <RBACTooltip
                            hidden={canWrite}
                            styles={rBACTooltipStyles}
                            displayedUserRole={displayedUserRole}
                            data-automation-id="multiLingualTitleLanguageToolTip"
                            componentSurroundedByToolTip={
                                <Toggle
                                    disabled={!canWrite || !!amlProjectPath}
                                    onText={localize(messages.enabledToggle)}
                                    checked={formState.fields.multiLingual.value}
                                    offText={localize(messages.notEnabledToggle)}
                                    ariaLabel={localize(messages.multiLingualLabel)}
                                    onChange={(_, checked) => {
                                        logToggleChange(
                                            serviceName,
                                            'Settings',
                                            'MultiLingual',
                                            checked ? 'Enabled' : 'Disabled',
                                            null,
                                            currentProject.id
                                        );
                                        updateFormState({ multiLingual: checked });
                                    }}
                                />
                            }
                        />
                    )}
                </Stack>
                <Separator />
                {currentResource && (
                    <Stack tokens={{ childrenGap: 15 }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            {renderTitles(localize(messages.azureResourceTitle))}
                            <CalloutButton content={localize(messages.azureResourceDescription)} />
                        </Stack>

                        {renderLabeledText(localize(messages.azureResourceLabel), currentResource.name)}
                        {canListResourceKeys &&
                            renderLabeledText(localize(messages.primaryKeyLabel), <ResourceKeyCell account={currentResource} />)}
                    </Stack>
                )}

                {FeatureToggles.AMLIntegration && [
                    <Separator />,

                    <Stack style={{ marginBottom: '80px' }} tokens={{ childrenGap: 15 }}>
                        <Stack style={{ width: '600px' }} tokens={{ childrenGap: 5 }}>
                            {renderTitles(localize(messages.amlConnectionTitle))}
                            <LuisText>
                                {localize(messages.amlConnectionDescription)}
                                &nbsp;
                                <ExternalLink href="https://go.microsoft.com/fwlink/?linkid=2257230">
                                    {localize(messages.amlConnectionDescriptionLink)}
                                </ExternalLink>
                            </LuisText>
                        </Stack>
                        {amlProjectPath ? (
                            getProjectInfoTracker.isRunning ? (
                                <>
                                    <Shimmer />
                                    <Shimmer width="75%" />
                                    <Shimmer width="50%" />
                                </>
                            ) : (
                                <Stack
                                    id="amlConnectionButtonId"
                                    tokens={{ childrenGap: childrenGapConstant }}
                                    styles={{ root: { marginBottom: '20px' } }}>
                                    <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                                        <LuisText fontWeight={fontWeight}>{localize(messages.connectionStatus)}</LuisText>
                                        <LuisText>
                                            &nbsp;
                                            {localize(messages.connected)}
                                        </LuisText>
                                    </Stack>
                                    <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                                        <LuisText fontWeight={fontWeight}>{localize(messages.labelingStatus)}</LuisText>
                                        {isLabelingLocked ? (
                                            <Stack>
                                                <LuisText>
                                                    &nbsp;
                                                    {localize(messages.labelingStatusInAml)}
                                                </LuisText>
                                                <ExternalLink href={amlProjectUrl}>{localize(messages.goToAml)}</ExternalLink>
                                            </Stack>
                                        ) : (
                                            <Stack>
                                                <LuisText>
                                                    &nbsp;
                                                    {localize(messages.labelingStatusInLS)}
                                                </LuisText>
                                                <ExternalLink
                                                    onClick={() =>
                                                        history.push({
                                                            pathname: routeTable.customText.dataTaggingPage(
                                                                currentProject.name,
                                                                currentProject.projectKind
                                                            ),
                                                            state: { tab: 'azureML' }
                                                        })
                                                    }>
                                                    {localize(messages.switchLabelingLocation)}
                                                </ExternalLink>
                                            </Stack>
                                        )}
                                    </Stack>

                                    <HorizontalTextRenderer
                                        title={localize(messages.azureBlobDataStore)}
                                        message={currentProject.containerName}
                                    />

                                    <HorizontalTextRenderer
                                        title={localize(messages.azureMLWorkspace)}
                                        message={amlConnectionInfo?.azureMLWorkspace}
                                    />

                                    <HorizontalTextRenderer
                                        title={localize(messages.projectName)}
                                        message={amlConnectionInfo?.projectName}
                                    />

                                    <HorizontalTextRenderer
                                        title={localize(messages.labelingTaskType)}
                                        message={amlConnectionInfo?.labelingTaskType}
                                    />

                                    <HorizontalTextRenderer title={localize(messages.language)} message={projectLanguage} />

                                    <HorizontalTextRenderer title={localize(messages.incrementalRefresh)} message={'Enabled'} />

                                    <HorizontalTextRenderer
                                        title={localize(messages.outsourcing)}
                                        message={amlConnectionInfo?.vendor || localize(messages.none)}
                                    />

                                    <HorizontalTextRenderer
                                        title={localize(messages.labelingInstruction)}
                                        message={
                                            (amlConnectionInfo?.labelingInstructionText !== 'stored externally' &&
                                                amlConnectionInfo?.labelingInstructionText) ||
                                            amlConnectionInfo?.labelingInstructionUrl
                                                ? localize(messages.provided)
                                                : localize(messages.none)
                                        }
                                    />

                                    <Label>{localize(messages.amlDisconnectionButtonTitle)}</Label>
                                    <DefaultButton
                                        style={{ width: '350px' }}
                                        text={localize(messages.amlDisconnectionButtonTitle)}
                                        onClick={showDisconnectAmlProjectModalOpen}
                                    />
                                    <BasicModal
                                        minWidth={416}
                                        onDismiss={hideDisconnectAmlProjectModalOpen}
                                        hidden={!isDisconnectAmlProjectModalOpen}
                                        modalProps={{ isModeless: true }}
                                        shouldRenderFooter={false}
                                        dialogContentProps={{ title: localize(messages.disconnectLabel) }}>
                                        <Stack tokens={{ padding: 10, childrenGap: 40 }}>
                                            <Stack>
                                                <LuisText>{localize(messages.disconnectDescription)}</LuisText>
                                            </Stack>
                                            <Stack tokens={{ childrenGap: 10 }} horizontal horizontalAlign="end">
                                                {disconnectTracker.isRunning ? <Spinner /> : null}
                                                <PrimaryButton text={localize(messages.disconnect)} onClick={handleAmlTermination} />
                                                <DefaultButton
                                                    text={localize(messages.cancel)}
                                                    onClick={hideDisconnectAmlProjectModalOpen}
                                                />
                                            </Stack>
                                        </Stack>
                                    </BasicModal>
                                </Stack>
                            )
                        ) : (
                            <>
                                <Stack styles={{ root: { marginBottom: '20px' } }}>
                                    <Label>{localize(messages.amlConnectionButtonTitle)}</Label>
                                    <DefaultButton
                                        style={{ width: '300px' }}
                                        text={localize(messages.amlConnectionButtonLabel)}
                                        onClick={handleOpenAzureMLConnectionModal}
                                        disabled={isMultiLingual}
                                        title={isMultiLingual ? localize(messages.multiLingual) : undefined}
                                    />
                                </Stack>
                                <AmlWarningModal
                                    hidden={!isLabelInstructionModalOpen}
                                    isExtractionProject={isExtractionProject}
                                    closeModal={hideLabelInstructionModal}
                                />
                            </>
                        )}
                        <AzureMLConnectionModal hidden={isAzureMLConnectionModalHidden} onDismiss={hideIsAzureMLConnectionModal} />
                    </Stack>
                ]}
            </Stack>
        </Page>
    );
});
